/// <reference types="vite/client" />

import { APP_VERSION_HEADER } from "@whatslly/commons/constants";

type PostRequest = {
  urlSuffix: string;
  pod?: string;
  body: string | (FormData & { getBody?: () => any });
  etoken?: string;
  requestId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchMethod?: any;
  token?: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};
type GetRequest = {
  urlSuffix: string;
  pod?: string;
  etoken?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchMethod?: any;
  token?: string;
  headers?: Record<string, string>;
  requestId?: string;
  timeout?: number;
  ignoreInterceptResponse?: boolean;
  signal?: AbortSignal;
};

// Used for dev env where there is no ingress
const INGRESS_PORTS: Record<string, number> = {
  hubspot: 8063,
  dynamics: 8059,
  integrations: 8008,
  "tuvis-messaging": 8008,
  notifications: 8019,
  codes: 8181,
  sfdc: 8181,
  analytics: 8283,
  rout: 8008,
  sidecars: 8020,
  auth: 8021,
  message: 8022,
  api: 9023,
  "analytics-producer": 8767,
  "messaging-ws": 8023,
  archive: 8026,
  "ai-chat": 8025,
  bots: 8031,
};

type interceptor = (response: Response) => Promise<void | { retry: { newEtoken?: string } }>;
const responseInterceptors: Array<{ interceptor: interceptor }> = [];
export const registerResponseInterceptor = (interceptor: interceptor) => {
  responseInterceptors.push({ interceptor });
};

// The desktop app registers its version once at startup so every authenticated request carries it,
// letting servers record the client version without needing a dedicated endpoint. Left unregistered by
// other clients: the server-side write only accepts an Electron user-agent, so it would be inert.
let registeredAppVersion: string | undefined;
export const registerAppVersion = (version: string) => {
  registeredAppVersion = version;
};

/** Exported for tests: the request helpers themselves need a Vite env, this seam does not. */
export function withAppVersion(headers: Record<string, string>) {
  if (registeredAppVersion) {
    headers[APP_VERSION_HEADER] = registeredAppVersion;
  }
  return headers;
}

async function interceptResponse(response: Response) {
  let result: undefined | void | { retry: { newEtoken?: string } };
  for (const { interceptor } of responseInterceptors) {
    result ??= await interceptor(response);
  }
  return result;
}

export const getHost = (urlSuffix: string, pod?: string) => {
  const VITE_API_MAP_TO_PORT_BY_PATH = import.meta.env.VITE_API_MAP_TO_PORT_BY_PATH;
  const VITE_API_FORCE_DEFAULT_POD = import.meta.env.VITE_API_FORCE_DEFAULT_POD;
  const VITE_API_BASE_HOST = import.meta.env.VITE_API_BASE_HOST;
  if (VITE_API_MAP_TO_PORT_BY_PATH && VITE_API_MAP_TO_PORT_BY_PATH !== "false") {
    const subsystem = urlSuffix.split("/")[1];
    const port = INGRESS_PORTS[subsystem];
    if (!port) {
      throw new Error(`No port found for subsystem ${subsystem}`);
    }
    return `${VITE_API_BASE_HOST}:${port}`;
  }
  if (VITE_API_FORCE_DEFAULT_POD && VITE_API_FORCE_DEFAULT_POD !== "false") {
    pod = import.meta.env.VITE_API_DEFAULT_POD?.toString();
  }
  pod = pod ?? import.meta.env.VITE_API_DEFAULT_POD?.toString();
  return `https://api${pod ? `-${pod}` : ""}.whatslly.com`;
};

export const postJSON = async <T = any>({
  urlSuffix,
  pod,
  body,
  etoken,
  requestId,
  fetchMethod = fetch,
  token,
  headers,
  signal,
}: PostRequest): Promise<T> => {
  const urlPrefix = getHost(urlSuffix, pod);
  headers = headers ?? (body instanceof FormData ? {} : { "Content-Type": "application/json; charset=utf-8" });
  const currentBody = body instanceof FormData && typeof body.getBody === "function" ? body.getBody() : body;

  if (etoken) {
    headers.Authorization = etoken;
  }

  if (token) {
    headers["X-Integrations-Token"] = token;
  }

  if (requestId) {
    headers["request-id"] = requestId;
  }

  const res = await fetchMethod(`${urlPrefix}${urlSuffix}`, {
    method: "POST",
    body: currentBody,
    headers: withAppVersion(headers),
    signal,
  });
  const interception = await interceptResponse(res);
  if (interception && interception.retry) {
    return postJSON({ urlSuffix, pod, body, etoken: interception.retry.newEtoken ?? etoken, requestId, fetchMethod, token, signal });
  }
  return res;
};

export const putJSON = async <T = any>({
  urlSuffix,
  pod,
  body,
  etoken,
  fetchMethod = fetch,
  requestId,
  token,
  headers,
  signal,
}: PostRequest): Promise<T> => {
  const urlPrefix = getHost(urlSuffix, pod);
  headers = headers ?? (body instanceof FormData ? {} : { "Content-Type": "application/json; charset=utf-8" });

  if (etoken) {
    headers.Authorization = etoken;
  }

  if (token) {
    headers["X-Integrations-Token"] = token;
  }

  if (requestId) {
    headers["request-id"] = requestId;
  }

  const res = await fetchMethod(`${urlPrefix}${urlSuffix}`, {
    method: "PUT",
    body,
    headers: withAppVersion(headers),
    signal,
  });
  const interception = await interceptResponse(res);
  if (interception && interception.retry) {
    return putJSON({ urlSuffix, pod, body, etoken: interception.retry.newEtoken ?? etoken, fetchMethod, requestId, token, signal });
  }
  return res;
};

export const get = async ({ urlSuffix, pod }: GetRequest) => {
  const urlPrefix = getHost(urlSuffix, pod);
  return fetch(`${urlPrefix}${urlSuffix}`, {
    method: "GET",
  });
};

export const fetchGet = async ({
  fetchMethod = fetch,
  url,
  headers,
  timeout,
  signal,
}: {
  url: string; // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchMethod?: any;
  headers?: Record<string, string>;
  timeout?: number;
  signal?: AbortSignal;
}) => {
  return await fetchMethod(url, {
    method: "GET",
    headers,
    timeout,
    signal,
  });
};

export const getJSON = async <T = any>({
  pod,
  etoken,
  urlSuffix,
  fetchMethod = fetch,
  token,
  requestId,
  timeout,
  headers,
  ignoreInterceptResponse,
  signal,
}: GetRequest): Promise<T> => {
  const urlPrefix = getHost(urlSuffix, pod);
  headers ??= {
    "Content-Type": "application/json; charset=utf-8",
  };

  if (etoken) {
    headers.Authorization = etoken;
  }
  if (token) {
    headers["X-Integrations-Token"] = token;
  }
  if (requestId) {
    headers["request-id"] = requestId;
  }
  const res = await fetchGet({
    fetchMethod,
    url: `${urlPrefix}${urlSuffix}`,
    headers: withAppVersion(headers),
    timeout,
    signal,
  });

  if (!ignoreInterceptResponse) {
    const interception = await interceptResponse(res);
    if (interception && interception.retry) {
      return getJSON({ urlSuffix, pod, etoken: interception.retry.newEtoken ?? etoken, fetchMethod, token, requestId, timeout, signal });
    }
  }

  return res;
};

export const deleteJSON = async <T = any>({
  urlSuffix,
  pod,
  body,
  etoken,
  requestId,
  fetchMethod = fetch,
  token,
  headers,
  signal,
}: PostRequest): Promise<T> => {
  const urlPrefix = getHost(urlSuffix, pod);
  headers = headers ?? (body instanceof FormData ? {} : { "Content-Type": "application/json; charset=utf-8" });

  if (etoken) {
    headers.Authorization = etoken;
  }

  if (token) {
    headers["X-Integrations-Token"] = token;
  }

  if (requestId) {
    headers["request-id"] = requestId;
  }

  const res = await fetchMethod(`${urlPrefix}${urlSuffix}`, {
    method: "DELETE",
    body,
    headers: withAppVersion(headers),
    signal,
  });
  const interception = await interceptResponse(res);
  if (interception && interception.retry) {
    return deleteJSON({ urlSuffix, pod, body, etoken: interception.retry.newEtoken ?? etoken, fetchMethod, requestId, token, signal });
  }
  return res;
};
